import React, { useRef, useState, useEffect, useCallback } from 'react'
import cn from 'classnames'

import Icon from '../Icon2'
import { AssetZoomableViewerProps } from './types'
import { getAssetIcon } from '../../utils'

import './AssetZoomableViewer.scss'

const AssetZoomableViewer: React.FC<AssetZoomableViewerProps> = (props) => {
  const {
    assetOptions,
    isError = false,
    brokenAssetSvg,
    className,
    zoomOptions,
    testId = '',
    containerOptions,
    previewIconOptions
  } = props
  const { assetUrl, assetTitle = '', assetAltText = '', paddingAroundAsset = 0 } = assetOptions || {}
  const { zoomValue, maxZoomMultiplier = 3 } = zoomOptions || {}
  const containerRef = useRef<HTMLDivElement>(null)
  const assetRef = useRef<HTMLImageElement>(null)
  const isImageAsset = assetOptions?.assetType === 'image'

  // Scale represents zoom level (1 = 100%)
  const [scale, setScale] = useState(1)
  const [naturalScale, setNaturalScale] = useState(1)
  const [isDragging, setIsDragging] = useState(false)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [startDragOffset, setStartDragOffset] = useState({ x: 0, y: 0 })
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 })
  const [assetLoaded, setAssetLoaded] = useState(false)
  const [assetDimensions, setAssetDimensions] = useState({ width: 0, height: 0 })
  const [isAssetRenderError, setIsAssetRenderError] = useState(isError || brokenAssetSvg?.length > 0)
  const [previewIconError, setPreviewIconError] = useState(false)

  // Effect to apply zoom based on zoomValue prop
  useEffect(() => {
    if (zoomValue !== undefined && naturalScale > 0) {
      // Convert slider value (0-100) to zoom scale relative to natural scale
      const minZoomMultiplier = 1
      // zoomValue 0 = natural scale (100%), zoomValue 100 = maxZoomMultiplier * 100% zoom
      const zoomMultiplier = minZoomMultiplier + (zoomValue / 100) * (maxZoomMultiplier - minZoomMultiplier)
      const newScale = naturalScale * zoomMultiplier
      setScale(newScale)

      // Reset position when zoomValue returns to 0
      if (zoomValue === 0 && assetDimensions.width > 0 && containerSize.width > 0) {
        const { x, y } = calculateInitialPosition(
          assetDimensions.width,
          assetDimensions.height,
          containerSize.width,
          containerSize.height
        )
        setPosition({ x, y })
      }
    }
  }, [zoomValue, naturalScale, assetDimensions, containerSize, paddingAroundAsset])

  // Calculate initial position to center the asset in container
  const calculateInitialPosition = (
    imgWidth: number,
    imgHeight: number,
    containerWidth: number,
    containerHeight: number
  ) => {
    containerWidth = containerOptions?.width || containerWidth
    containerHeight = containerOptions?.height || containerHeight
    // Account for padding when calculating available space
    const availableWidth = containerWidth - paddingAroundAsset * 2
    const availableHeight = containerHeight - paddingAroundAsset * 2

    // Calculate scale to fit asset in available space (container minus padding)
    const scaleX = availableWidth / imgWidth
    const scaleY = availableHeight / imgHeight
    const scale = Math.min(scaleX, scaleY, 1)

    // Since transform-origin is center, we need to position the center of the original asset at the center of the available space of container.
    // The transform translate moves from the center point of the image
    const x = (containerWidth - imgWidth) / 2
    const y = (containerHeight - imgHeight) / 2

    return { x, y, scale }
  }

  const handleAssetLoad = useCallback(() => {
    // Add timeout to ensure any reset effects run first
    setTimeout(() => {
      if (assetRef.current && containerRef.current) {
        const img = assetRef.current
        const imgWidth = img.naturalWidth
        const imgHeight = img.naturalHeight

        setAssetDimensions({ width: imgWidth, height: imgHeight })

        // Calculate initial position to center the asset
        const { x, y, scale } = calculateInitialPosition(imgWidth, imgHeight, containerSize.width, containerSize.height)

        // Store the natural scale for zoom calculations
        setNaturalScale(scale)
        setPosition({ x, y })
        // Initially set zoom to natural scale (fit to container)
        setScale(scale)
      }
      setAssetLoaded(true)
      setIsAssetRenderError(false)
    }, 0)
  }, [containerSize.width, containerSize.height, paddingAroundAsset, containerOptions])

  // Handle immediate loading for base64 images - helps in replacement scenarios
  useEffect(() => {
    if (assetRef.current && assetRef.current.complete && assetRef.current.naturalWidth > 0 && containerSize.width > 0) {
      handleAssetLoad()
    }
  }, [assetUrl, containerSize.width, containerSize.height, handleAssetLoad])

  const handleAssetError = () => {
    setIsAssetRenderError(true)
    setAssetLoaded(false)
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    // Only allow dragging when zoom value is greater than 0
    if (!zoomValue || zoomValue <= 0 || !isImageAsset) return
    e.preventDefault()
    setIsDragging(true)
    setStartDragOffset({ x: e.clientX - position.x, y: e.clientY - position.y })
  }

  useEffect(() => {
    // throttled mousemove for performance
    let animationFrame: number
    const throttledMouseMove = (e: MouseEvent) => {
      if (!isDragging) return
      animationFrame = requestAnimationFrame(() => {
        setPosition({ x: e.clientX - startDragOffset.x, y: e.clientY - startDragOffset.y })
      })
    }

    document.addEventListener('mousemove', throttledMouseMove)
    document.addEventListener('mouseup', () => setIsDragging(false))
    return () => {
      document.removeEventListener('mousemove', throttledMouseMove)
      document.removeEventListener('mouseup', () => setIsDragging(false))
      cancelAnimationFrame(animationFrame)
    }
  }, [isDragging, startDragOffset])

  // Update container size on mount and resize
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect()
        setContainerSize({ width: containerOptions?.width || width, height: containerOptions?.height || height })
      }
    }
    updateSize()
    window.addEventListener('resize', updateSize)
    return () => window.removeEventListener('resize', updateSize)
  }, [])

  // Recalculate position when dimensions or asset load changes
  useEffect(() => {
    if (assetLoaded && assetDimensions.width > 0 && containerSize.width > 0) {
      const { x, y, scale } = calculateInitialPosition(
        assetDimensions.width,
        assetDimensions.height,
        containerSize.width,
        containerSize.height
      )
      setNaturalScale(scale)
      setPosition({ x, y })
      setScale(scale)
    }
  }, [containerSize, assetDimensions, assetLoaded, paddingAroundAsset, containerOptions])

  // Reset states when asset URL changes
  useEffect(() => {
    setAssetLoaded(false)
    setIsAssetRenderError(false)
    setPreviewIconError(false)
    setPosition({ x: 0, y: 0 })
    setScale(1)
    setNaturalScale(1)
    setAssetDimensions({ width: 0, height: 0 })
  }, [assetUrl])

  const canDrag = zoomValue && zoomValue > 0 && isImageAsset && !isAssetRenderError && !isError

  return (
    <div
      className={cn('AssetZoomableViewer__container', className)}
      ref={containerRef}
      data-test-id={`cs-${testId}-main-container`}>
      <div
        data-test-id={`cs-${testId}-asset-wrapper`}
        className={cn('AssetZoomableViewer__container__asset_wrapper')}
        onMouseDown={handleMouseDown}
        style={{
          ...(!isAssetRenderError && !isError ? { padding: `${paddingAroundAsset / 16}rem` } : {}),
          cursor: !canDrag ? 'default' : isDragging ? 'grabbing' : 'grab',
          width: containerSize.width,
          height: containerSize.height
        }}>
        {isAssetRenderError || isError ? (
          // Render broken asset state
          <div className={cn('broken_asset_container')}>
            <Icon
              icon={brokenAssetSvg ? brokenAssetSvg : 'NoAssetsSuggestionsDataState'}
              testId={`cs-${testId}-broken-state-${brokenAssetSvg?.length > 0 ? 'brokenAssetSvg' : 'default'}`}
            />
          </div>
        ) : isImageAsset ? (
          // Render image asset with zoom and drag functionality
          <img
            data-asset-title={assetTitle}
            data-test-id={`cs-${testId}-asset-image`}
            className={cn('asset_image_format')}
            ref={assetRef}
            src={assetUrl}
            alt={assetAltText}
            onLoad={handleAssetLoad}
            onError={handleAssetError}
            style={{
              transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
              opacity: assetLoaded ? 1 : 0,
              transition: assetLoaded ? 'none' : 'opacity 0.3s ease',
              transformOrigin: 'center',
              visibility: assetLoaded ? 'visible' : 'hidden'
            }}
          />
        ) : (
          // Render non-image asset with icon or preview icon
          <div className={cn('asset_non_image_format')} data-asset-title={assetTitle}>
            {previewIconOptions?.showPreviewIcon && !previewIconError ? (
              <img
                src={previewIconOptions.url}
                alt={assetAltText}
                data-asset-title={assetTitle}
                data-test-id={`cs-${testId}-asset-non-image-preview-icon`}
                className={cn('asset_non_image_format__preview_icon')}
                onError={() => setPreviewIconError(true)}
              />
            ) : (
              <Icon
                tooltipContent={assetTitle}
                width={80}
                height={80}
                icon={getAssetIcon(assetOptions.assetType, 'v2')}
                version="v2"
                testId={`cs-${testId}-asset-non-image`}
              />
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default React.memo(AssetZoomableViewer)
